// TENANT-5036: Split out of `add-property-sections-config.js` so that
// `*-routing.js` files (which are eagerly imported app-wide to build the
// route table) can reference `ADD_PROPERTY_SECTION` without pulling in the
// full `sectionsConfig` object and its 12 `lazy()`-wrapped property-setup
// page definitions - none of which the routing table itself needs, but
// which cost ~10KB in every visitor's eager main bundle when imported from
// there. `add-property-sections-config.js` re-exports both constants below
// so none of its other (already-lazy-chunk) consumers need to change.
export const ADD_PROPERTY_SECTION = Object.freeze({
    CONNECT_PROPERTIES: 'CONNECT_PROPERTIES',
    CONNECT_PROPERTY: 'CONNECT_PROPERTY',
    SETUP_EXTERNAL: 'SETUP_EXTERNAL',
    SETUP_NATIVE: 'SETUP_NATIVE',
})

export const SETUP_PAGE = Object.freeze({
    ADDRESS: 'address',
    BASICS: 'basics',
    COMPLETE: 'complete',
    DESCRIPTION: 'description',
    DETAILS: 'details',
    DISCOUNT: 'discount',
    INSURANCE: 'insurance',
    NAME: 'name',
    PHOTOS: 'photos',
    POLICIES: 'policies',
    PRICING: 'pricing',
    READY: 'ready',
    SETTINGS: 'settings',
})

/**
 * Which of the two wizards a listing is going through.
 *
 * These three declarations below used to live in `utils/analytics-host-flow.js`
 * because the PostHog events needed them first, which left every non-analytics
 * consumer importing an analytics module — and its PostHog, GA and session-store
 * dependencies — to learn a fact about the wizard. They are wizard vocabulary, so
 * they belong beside the wizard's other constants; `analytics-host-flow.js` now
 * re-exports them, so its own consumers are unaffected.
 */
export const WIZARD_FLOW = Object.freeze({
    CONNECTED: 'connected',
    NATIVE: 'native',
})

/**
 * Map the internal `addPropertySectionKey` to the PRD `flow_type` enum.
 * SETUP_NATIVE -> 'native', SETUP_EXTERNAL -> 'connected'. Unrecognized keys
 * (CONNECT_PROPERTY, CONNECT_PROPERTIES, etc.) default to 'connected' since
 * those are the connected-flow pre-wizard sections.
 */
export const flowTypeFromSectionKey = (sectionKey) => {
    if (sectionKey === ADD_PROPERTY_SECTION.SETUP_NATIVE) return WIZARD_FLOW.NATIVE
    return WIZARD_FLOW.CONNECTED
}

/** The final, non-fillable step of each wizard. */
export const WIZARD_TERMINAL_STEP = Object.freeze({
    CONNECTED: SETUP_PAGE.READY,
    NATIVE: SETUP_PAGE.COMPLETE,
})

/**
 * The connect flow's screens, named for the header's "Need Help?" button
 * (TENANT-5616). The connect pages keep their screens as component state on one
 * URL (`STEPS` in `use-property-connection-controller.js`), so unlike the wizard
 * steps these are not URL segments — the page reports the current one to the
 * template context and the help button reads it from there. The terminal
 * "connected" screen has no key: it carries no help.
 */
export const CONNECT_STEP = Object.freeze({
    CONNECTION_CODE: 'connection-code',
    LISTING_URL: 'listing-url',
})

/** Whether a section key names one of the two connect pages rather than a setup wizard. */
export const isConnectSection = (sectionKey) => (
    sectionKey === ADD_PROPERTY_SECTION.CONNECT_PROPERTY
    || sectionKey === ADD_PROPERTY_SECTION.CONNECT_PROPERTIES
)
